import axios from "axios";
import jwt_decode from "jwt-decode";

import { api } from "../../apis";
import { helperFunction } from "../../constant/helper";
import { types } from "../type";
import setAuthToken from "../../utils/setAuthToken";
import { authSessionPusher } from "../../utils/pusher";

// set loading
const setAuthLoading = (data) => (dispatch) => {
  dispatch({
    type: types.AuthTypes.AUTH_SET_LOADING,
    payload: data
  });
};

// set checkTokenLoading
const tokenLoading = (data) => (dispatch) => {
  dispatch({
    type: types.AuthTypes.TOKEN_LOADING,
    payload: data
  });
};

// set Token found
const checkToken = () => (dispatch) => {
  dispatch(tokenLoading(true));
  dispatch({
    type: types.AuthTypes.TOKEN_FOUND,
    payload: localStorage.cmcAttendanceJwtToken
  });
};

// set logged in user
const setCurrentUser = (decoded) => {
  return {
    type: types.AuthTypes.SET_CURRENT_USER,
    payload: decoded
  };
};

// clear result
const clearAuthResult = () => (dispatch) => {
  dispatch({
    type: types.AuthTypes.AUTH_CLEAR_RESULT
  });
};

// clear error
const clearAuthErrors = () => (dispatch) => {
  dispatch({
    type: types.AuthTypes.AUTH_CLEAR_ERRORS
  });
};

// set local storage
const setLocalStorage = async (
  token,
  session,
  loginType,
  loginInfo,
  refreshToken
) => {
  const { id, workerId, workerAadharCardNumber, employeeAccess } =
    jwt_decode(token);
  localStorage.setItem("id", id);
  localStorage.setItem("loginType", loginType);
  localStorage.setItem("loginSessionId", session._id);
  localStorage.setItem("workerId", workerId);
  localStorage.setItem(
    "employeeAccess",
    JSON.stringify(employeeAccess)
  );
  localStorage.setItem(
    "workerAadharCardNumber",
    workerAadharCardNumber
  );
  localStorage.setItem("cmcAttendanceJwtToken", token);
  localStorage.setItem("cmcAttendanceJwtRefreshToken", refreshToken);
  localStorage.setItem("browserDetails", loginInfo.deviceDetails);
};

// checkIfSessionValid
const checkIfSessionValid =
  (sessionId, loginType) => async (dispatch) => {
    try {
      const { baseUrlNode } = await helperFunction.checkLoginType(
        loginType
      );
      const sessionCheckSuccess = await axios.get(
        `${baseUrlNode}${api.Auth_Api.CHECK_SESSION_API}/${sessionId}`
      );
      const { isActive } = sessionCheckSuccess.data;
      if (!isActive) {
        dispatch(logoutUser());
      }
    } catch (sessionCheckError) {
      dispatch(logoutUser());

      const errorData = {
        data: sessionCheckError.response.data,
        status: sessionCheckError.response.status
      };
      dispatch({
        type: types.AuthTypes.AUTH_GET_ERRORS,
        payload: errorData
      });
    }
  };

// on idle screen
const onIdleScreen =
  (sessionId, lastActiveOn, loginType) => async (dispatch) => {
    try {
      const { baseUrlNode } = await helperFunction.checkLoginType(
        loginType
      );
      await axios.post(
        `${baseUrlNode}${api.Auth_Api.CHANGE_SESSION_STATUS}/${sessionId}`,
        lastActiveOn
      );
      dispatch(logoutUser());
    } catch (sessionStatusError) {
      const errorData = {
        data: sessionStatusError.response.data,
        status: sessionStatusError.response.status
      };
      dispatch({
        type: types.AuthTypes.AUTH_GET_ERRORS,
        payload: errorData
      });
    }
  };

// login
const loginUser = (loginInfo, loginType) => async (dispatch) => {
  try {
    const { baseUrlNode } = await helperFunction.checkLoginType(
      loginType
    );

    const loginSuccess = await axios.post(
      `${baseUrlNode}${api.Auth_Api.LOGIN_API}`,
      loginInfo
    );
    const {
      token = "",
      session = {},
      refreshToken = ""
    } = loginSuccess.data;
    const { id, workerId, workerAadharCardNumber } =
      jwt_decode(token);

    if (id && workerId && workerAadharCardNumber && loginType) {
      await setLocalStorage(
        token,
        session,
        loginType,
        loginInfo,
        refreshToken
      );
      setAuthToken(token);
      const decoded = jwt_decode(token);
      dispatch(setCurrentUser(decoded));
      authSessionPusher(session._id);
    } else {
      dispatch(setAuthLoading(false));
      const errorData = {
        data: { message: "Not able to login" },
        status: 400
      };
      dispatch({
        type: types.AuthTypes.AUTH_GET_ERRORS,
        payload: errorData
      });
    }
  } catch (loginError) {
    const errorData = {
      data: loginError.response.data,
      status: loginError.response.status
    };

    dispatch({
      type: types.AuthTypes.AUTH_GET_ERRORS,
      payload: errorData
    });
  }
};

// logout user
const logoutUser = () => async (dispatch) => {
  try {
    const loginType = localStorage.loginType;
    const { baseUrlNode } = await helperFunction.checkLoginType(
      loginType
    );
    if (localStorage.loginSessionId) {
      const logoutInfo = {
        workerId: localStorage.workerId,
        sessionId: localStorage.loginSessionId
      };
      await axios.post(
        `${baseUrlNode}${api.Auth_Api.LOGOUT_API}`,
        logoutInfo
      );
      localStorage.removeItem("id");
      localStorage.removeItem("loginType");
      localStorage.removeItem("workerId");
      localStorage.removeItem("cmcAttendanceJwtToken");
      localStorage.removeItem("workerAadharCardNumber");
      localStorage.removeItem("locationName");
      localStorage.removeItem("moduleType");
      localStorage.removeItem("locationId");
      localStorage.removeItem("loginSessionId");
      localStorage.removeItem("employeeAccess");
      setAuthToken(false);
      dispatch(setCurrentUser({}));
    } else {
      localStorage.removeItem("id");
      localStorage.removeItem("loginType");
      localStorage.removeItem("workerId");
      localStorage.removeItem("cmcAttendanceJwtToken");
      localStorage.removeItem("workerAadharCardNumber");
      localStorage.removeItem("locationName");
      localStorage.removeItem("moduleType");
      localStorage.removeItem("locationId");
      localStorage.removeItem("employeeAccess");
      setAuthToken(false);
      dispatch(setCurrentUser({}));
    }
  } catch (logoutError) {
    const errorData = {
      data: logoutError.response.data,
      status: logoutError.response.status
    };
    dispatch({
      type: types.AuthTypes.AUTH_GET_ERRORS,
      payload: errorData
    });
  }
};

// excel / csv
const registerExcel = (formData, loginType) => async (dispatch) => {
  try {
    const { baseUrlPython } = await helperFunction.checkLoginType(
      loginType
    );
    const registerExcelData = await axios.post(
      `${baseUrlPython}${api.Auth_Api.REGISTER_EXCEL_API}`,
      formData
    );
    dispatch({
      type: types.AuthTypes.REGISTER_CSV,
      payload: registerExcelData.data
    });
  } catch (excelRegisterError) {
    const errorData = {
      data: excelRegisterError.response.data,
      status: excelRegisterError.response.status
    };
    dispatch({
      type: types.AuthTypes.AUTH_GET_ERRORS,
      payload: errorData
    });
  }
};

export {
  loginUser,
  logoutUser,
  onIdleScreen,
  registerExcel,
  setAuthLoading,
  setCurrentUser,
  clearAuthErrors,
  clearAuthResult,
  checkIfSessionValid,
  tokenLoading,
  checkToken
};
